import { useNavigate, useSearch } from "@tanstack/react-router";
import { Building2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Page, PageHeader } from "@/app";
import { Button, Card, EmptyState, ErrorState, GradeBadge, Input, Meter, Money, RelativeTime, Select, Skeleton, Table, TBody, TD, TH, THead, TR } from "@/design";
import { useAdminWorkspaces, type AdminWorkspace, type AdminWorkspaceFilter, type AdminWorkspaceFlag, type AdminWorkspaceStatus, type WorkspacePlan } from "@/lib/api";
import { moneyAmount } from "@/lib/format";
import { flagMeta, Pill, planLabel, workspaceStatus } from "../-shared/meta";

export interface TenantsSearch {
  q?: string;
  plan?: WorkspacePlan;
  status?: AdminWorkspaceStatus;
  flag?: AdminWorkspaceFlag;
  sort?: NonNullable<AdminWorkspaceFilter["sort"]>;
}

const ANY = "any";
const SORTS: { value: NonNullable<TenantsSearch["sort"]>; label: string }[] = [
  { value: "spend", label: "Spend this month" },
  { value: "last_deploy", label: "Last deploy" },
  { value: "created", label: "Newest" },
  { value: "name", label: "Name" },
];

export function validateTenantsSearch(s: Record<string, unknown>): TenantsSearch {
  const pick = <T extends string>(v: unknown, allowed: readonly T[]) => (typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : undefined);
  return {
    ...(typeof s.q === "string" && s.q ? { q: s.q } : {}),
    ...(pick(s.plan, Object.keys(planLabel) as WorkspacePlan[]) ? { plan: s.plan as WorkspacePlan } : {}),
    ...(pick(s.status, Object.keys(workspaceStatus) as AdminWorkspaceStatus[]) ? { status: s.status as AdminWorkspaceStatus } : {}),
    ...(pick(s.flag, Object.keys(flagMeta) as AdminWorkspaceFlag[]) ? { flag: s.flag as AdminWorkspaceFlag } : {}),
    ...(pick(s.sort, SORTS.map((x) => x.value)) ? { sort: s.sort as TenantsSearch["sort"] } : {}),
  };
}

export function TenantsScreen() {
  const search = useSearch({ from: "/admin/tenants/" });
  const navigate = useNavigate({ from: "/admin/tenants/" });
  const [text, setText] = useState(search.q ?? "");
  const set = (patch: Partial<TenantsSearch>) => void navigate({ search: (s: TenantsSearch) => ({ ...s, ...patch }), replace: true });

  // Debounce typing into the URL.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? "") !== text) set({ q: text || undefined });
    }, 250);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = useAdminWorkspaces({ ...search, sort: search.sort ?? "spend", limit: 100 });
  const filtered = !!(search.q || search.plan || search.status || search.flag);

  return (
    <Page width="wide">
      <PageHeader title="Tenants" description="Every workspace on Guv: who owns it, what it runs, what it pays and anything odd about it." meta={q.data && <span className="text-sm text-muted">{q.data.total} {filtered ? "match" : "in total"}</span>} />

      <div className="mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filters">
        <Input className="w-full sm:w-72" leading={<Search />} placeholder="Name, slug, owner email or ws_ id" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search tenants" />
        <Select
          className="w-36"
          aria-label="Status"
          value={search.status ?? ANY}
          onValueChange={(v) => set({ status: v === ANY ? undefined : (v as AdminWorkspaceStatus) })}
          options={[{ value: ANY, label: "Any status" }, ...Object.entries(workspaceStatus).map(([value, m]) => ({ value, label: m.label }))]}
        />
        <Select
          className="w-32"
          aria-label="Plan"
          value={search.plan ?? ANY}
          onValueChange={(v) => set({ plan: v === ANY ? undefined : (v as WorkspacePlan) })}
          options={[{ value: ANY, label: "Any plan" }, ...Object.entries(planLabel).map(([value, label]) => ({ value, label }))]}
        />
        <Select
          className="w-44"
          aria-label="Flag"
          value={search.flag ?? ANY}
          onValueChange={(v) => set({ flag: v === ANY ? undefined : (v as AdminWorkspaceFlag) })}
          options={[{ value: ANY, label: "Any flag" }, ...Object.entries(flagMeta).map(([value, m]) => ({ value, label: m.label }))]}
        />
        <Select className="w-44" aria-label="Sort by" value={search.sort ?? "spend"} onValueChange={(v) => set({ sort: v as TenantsSearch["sort"] })} options={SORTS} />
        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setText("");
              void navigate({ search: { sort: search.sort }, replace: true });
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} title="We could not load tenants" />
      ) : !q.data ? (
        <Skeleton className="h-96 w-full rounded-xl" />
      ) : q.data.data.length === 0 ? (
        <EmptyState bordered icon={<Building2 />} title={filtered ? "No tenants match" : "No tenants yet"} description={filtered ? "Try fewer filters." : "Workspaces appear here as people sign up."} />
      ) : (
        <TenantTable rows={q.data.data} />
      )}
    </Page>
  );
}

function TenantTable({ rows }: { rows: AdminWorkspace[] }) {
  const navigate = useNavigate();
  const open = (w: AdminWorkspace) => void navigate({ to: "/admin/tenants/$workspaceId", params: { workspaceId: w.id } });
  return (
    <Card className="p-0">
      <Table>
        <THead>
          <TR>
            <TH>Tenant</TH>
            <TH>Plan</TH>
            <TH>Owner</TH>
            <TH className="text-right">Projects</TH>
            <TH className="min-w-40">Spend this month</TH>
            <TH>Flags</TH>
            <TH>Last deploy</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((w) => {
            const spend = moneyAmount(w.spend_mtd);
            const cap = w.cap ? moneyAmount(w.cap) : null;
            return (
              <TR key={w.id} interactive tabIndex={0} onClick={() => open(w)} onKeyDown={(e) => e.key === "Enter" && open(w)} aria-label={`Open ${w.name}`}>
                <TD>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text">{w.name}</span>
                    <GradeBadge grade={w.worst_safety_grade ?? undefined} size="sm" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-subtle">
                    <span className="whitespace-nowrap font-mono">{w.slug}</span>
                    <Pill meta={workspaceStatus[w.status]} />
                  </div>
                </TD>
                <TD className="text-sm">{planLabel[w.plan]}</TD>
                <TD className="max-w-48 truncate text-sm text-muted">{w.owner?.email ?? "–"}</TD>
                <TD className="text-right text-sm tabular">
                  {w.projects}
                  <div className="whitespace-nowrap text-xs text-subtle">{w.services_running} running</div>
                </TD>
                <TD className="min-w-52">
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <Money value={spend} className="font-medium text-text" />
                    <span className="whitespace-nowrap text-xs text-subtle">
                      → <Money value={w.forecast_month} whole />
                      {cap != null && (
                        <>
                          {" "}
                          / <Money value={cap} whole />
                        </>
                      )}
                    </span>
                  </div>
                  {cap != null && <Meter className="mt-1.5" size="sm" value={spend} max={cap} forecast={moneyAmount(w.forecast_month)} label={`${w.name} spend against its cap`} />}
                </TD>
                <TD>
                  <div className="flex max-w-56 flex-wrap gap-1">
                    {w.flags.map((f) => (
                      <Pill key={f} meta={flagMeta[f]} />
                    ))}
                  </div>
                </TD>
                <TD className="whitespace-nowrap text-sm text-muted">
                  <RelativeTime value={w.last_deploy_at} />
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </Card>
  );
}
